import { useContext, useEffect, useMemo, useState } from 'react'

import {
  Accordion,
  Box,
  Button,
  Icon,
  Slider,
  Text,
} from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { Card } from '../../../../components/Card/card.comp'
import { GlobalDataContext } from '../../../../contexts/globalData'
import { IOrderProductionStepsResponse } from '../../../../sdk/order-production/order-production.types'
import {
  IItemShortTreeTraceability,
  IItemTraceability,
  IItemsStatisticsDTO,
  ItemRawStatisticsDataDTO,
} from '../../../../sdk/traceability/traceability.types'
import { sdk } from '../../../../services/sdk'
import { getItemCardProps } from '../../origin-invoice.utils'
import { BlockchainTreeModal } from '../BlockchainTreeModal/blockchain-tree-modal.comp'
import { OrderProductionStepsModal } from '../OrderProductionStepsModal/order-production-steps-modal.comp'
import { TraceabilityModal } from '../TraceabilityModal/traceability-modal.comp'

import { InvoiceItemProps } from './invoice-item.types'

export const InvoiceItem = ({ item }: InvoiceItemProps) => {
  const [open, setOpen] = useState(false)

  const [openBlockchainModal, setOpenBlockchainModal] = useState(false)

  const [itemStatistics, setItemStatistics] = useState<IItemsStatisticsDTO>()
  const [itemStatisticsData, setItemStatisticsData] =
    useState<ItemRawStatisticsDataDTO>()
  const [itemOrderProductionSteps, setItemOrderProductionSteps] =
    useState<IOrderProductionStepsResponse>()
  const [itemBlockchainTreeTraceability, setItemBlockchainTreeTraceability] =
    useState<IItemShortTreeTraceability>()
  const [traceability, setTraceability] = useState<IItemTraceability[]>()
  const { blockchainNetwork } = useContext(GlobalDataContext)

  const handleToggle = () => {
    setOpen(!open)
  }

  const getItemData = async (itemId: string) => {
    try {
      const [
        statisticsResponse,
        statisticsDataResponse,
        blockchainTreeResponse,
        traceabilityResponse,
      ] = await Promise.all([
        sdk.traceability.itemStatistics(itemId),
        sdk.traceability.itemStatisticsData(itemId),
        sdk.traceability.blockchainTreeShort(itemId),
        sdk.traceability.item(itemId),
      ])

      setItemStatistics(statisticsResponse)
      setItemStatisticsData(statisticsDataResponse)
      setItemBlockchainTreeTraceability(blockchainTreeResponse)
      setTraceability(traceabilityResponse)
    } catch (err) {}
    try {
      const orderProductionStepsResponse =
        await sdk.orderProduction.getOrderProductionSteps(itemId)

      setItemOrderProductionSteps(orderProductionStepsResponse)
    } catch (err) {}
  }

  useEffect(() => {
    if (open && !itemStatistics) {
      getItemData(item.id)
    }
  }, [open])

  const cardProps = useMemo(
    () => getItemCardProps(itemStatistics, itemStatisticsData),
    [itemStatistics, itemStatisticsData],
  )

  const theme = useTheme()

  return (
    <Accordion
      onToggle={handleToggle}
      header={
        <Box gap={1} flexWrap="wrap">
          <Box gap={1} alignItems="center" marginRight={4} maxWidth="100%">
            <Text size="small" color="grey_3">
              Código do SKU
            </Text>
            <Text>{item.code}</Text>
          </Box>
          <Box gap={1} alignItems="center">
            <Text size="small" color="grey_3">
              Quantidade
            </Text>
            <Text>{item.quantity}</Text>
          </Box>
        </Box>
      }
    >
      <Box
        backgroundColor="grey_5"
        borderRadius={theme.border_radius.box}
        padding={3}
        direction="column"
      >
        <Box justifyContent="space-between" width="100%">
          <Text fontWeight="bold" size="small" color="grey_2">
            Descrição
          </Text>
          <Box gap={2}>
            <TraceabilityModal data={traceability} />
            <Button
              variant="text"
              padding="0"
              onClick={() => setOpenBlockchainModal(true)}
            >
              <Icon name="link" size={24} color="secondary" />
            </Button>
            <BlockchainTreeModal
              data={itemBlockchainTreeTraceability}
              linkUrlExplorer={blockchainNetwork?.urlExplorer}
              authInfo={encodeURIComponent(
                JSON.stringify(blockchainNetwork?.explorerLogin),
              )}
              open={openBlockchainModal}
              setOpen={setOpenBlockchainModal}
            />
            <OrderProductionStepsModal data={itemOrderProductionSteps} />
          </Box>
        </Box>
        <Text>{item.description}</Text>

        <Box width="100%" marginTop={2}>
          <Slider buttonPosition="side" sizeToScroll={200}>
            {cardProps.map((props, index) => (
              <Card {...props} key={`item-card-${index}`} />
            ))}
          </Slider>
        </Box>
      </Box>
    </Accordion>
  )
}
